import { formatNumber } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import { Error, isError } from '../models';
import { Validators } from '../utils/validators';

@Injectable({
	providedIn: 'root',
})
export class ErrorService {
	protected translations: any;

	protected errors: Record<string, Record<string, string | ((data: any) => string)>> = {
		hu: {
			unknown_error: 'Ismeretlen hiba!',
			'app:error': 'Ismeretlen hiba történt! Kérjük próbáld újra!',
			'parcel_number:invalid': 'Nem megfelelő formátumú csomag azonosító!',
			'parcel:notFound': 'A csomagszám nem található.',
			'parcel:not_found': 'A csomagszám nem található.',

			'name:required': 'Kötelező mező.',
			'email:required': 'Kötelező mező.',
			'email:invalidEmail': 'A megadott email cím nem megfelelő.',
			'email:email': 'A megadott email cím nem megfelelő.',
			'mobile:required': 'Kötelező mező.',
			'mobile:invalidPhone': `Kérjük, ellenőrizze a megadott telefonszámot. (Pl.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:phoneNumber': `Kérjük, ellenőrizze a megadott telefonszámot. (Pl.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:myglsPhoneNumber': `Kérjük, ellenőrizze a megadott telefonszámot.`,
			'mobile:myglsPhoneNumber.any': `Kérjük, ellenőrizze a megadott telefonszámot. (Pl.: ${Validators.getMyglsPhoneNumberExample('any')})`,
			'mobile:myglsPhoneNumber.hu': `Kérjük, ellenőrizze a megadott telefonszámot. (Magyarország esetén: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:myglsPhoneNumber.sk': `Kérjük, ellenőrizze a megadott telefonszámot. (Szlovákia esetén: ${Validators.getMyglsPhoneNumberExample('sk')})`,
			'mobile:myglsPhoneNumber.cz': `Kérjük, ellenőrizze a megadott telefonszámot. (Csehország esetén: ${Validators.getMyglsPhoneNumberExample('cz')})`,
			'mobile:myglsPhoneNumber.ro': `Kérjük, ellenőrizze a megadott telefonszámot. (Románia esetén: ${Validators.getMyglsPhoneNumberExample('ro')})`,
			'mobile:myglsPhoneNumber.si': `Kérjük, ellenőrizze a megadott telefonszámot. (Szlovénia esetén: ${Validators.getMyglsPhoneNumberExample('si')})`,
			'mobile:myglsPhoneNumber.hr': `Kérjük, ellenőrizze a megadott telefonszámot. (Horvátország esetén: ${Validators.getMyglsPhoneNumberExample('hr')})`,

			// frontend validation
			'password:required': 'Kötelező mező.',
			'password:weakPassword': 'A megadott jelszó nem elég erős.',
			'password:weakPassword:length': 'A jelszó túl rövid, minimum 8 karakter.',
			'password:weakPassword:number': 'A jelszónak tartalmaznia kell számot.',
			'password:weakPassword:lowercase': 'A jelszónak tartalmaznia kell kisbetűt.',
			'password:weakPassword:uppercase': 'A jelszónak tartalmaznia kell nagybetűt.',
			'password:weakPassword:symbol': 'A jelszónak tartalmaznia kell speciális karaktert.',
			'passwordConfirmation:required': 'Kötelező mező.',
			'passwordConfirmation:passwordMismatch': 'A két jelszó nem egyezik meg.',
			// backend validation
			'password:numbers:required': 'A jelszónak tartalmaznia kell számot.',
			'password:mixedCase:required': 'A jelszónak tartalmaznia kell kis- és nagybetűt.',
			'password:symbols:required': 'A jelszónak tartalmaznia kell speciális karaktert.',
			'password:recentlyUsed': 'Ezt a jelszót már használta a közelmúltban.',
			'password:compromised': 'A megadott jelszó nem elég erős.',
			'password:password:numbers:required': 'A jelszónak tartalmaznia kell számot.',
			'password:password:mixedCase:required': 'A jelszónak tartalmaznia kell kis- és nagybetűt.',
			'password:password:symbols:required': 'A jelszónak tartalmaznia kell speciális karaktert.',
			'password:password:recentlyUsed': 'Ezt a jelszót már használta a közelmúltban.',
			'password:password:compromised': 'A megadott jelszó nem elég erős.',

			'bankAccountHolder:required': 'A számlatulajdonos nevének megadása kötelező.',
			'bankAccountNumber:required': 'A bankszámlaszám megadása kötelező.',
			'bankAccountNumber:invalidFormat': 'A bankszámlaszám formátuma nem megfelelő. (Kétszer vagy háromszor 8 számjegy, opcionálisan kötőjellel elválasztva.)',
			'bankAccountNumber:invalid': 'A megadott bankszámlaszám nem tűnik érvényes magyar bankszámlaszámnak.',

			'taxNumber:taxNumber': 'Hibás adószám. A megfelelő formátum: 12345678-1-12',
			'zipCode:zipCode': 'Az irányítószám mező csak számokat tartalmazhat.',
			'zipCode:zipCode:invalidFormat': data => `Hibás formátum. (${data['format']})`,
			'zipCode:minLength': data => `Az irányítószám min. ${data['minLength']} karakter.`,
			'zipCode:maxLength': data => `Az irányítószám max. ${data['maxLength']} karakter.`,
			'zipCode:deliveryAddressZipCode:restricted': 'A megadott irányítószámra nem küldhető csomag.',
			'houseNumber:houseNumber': 'A házszám mező csak számokat tartalmazhat.',
			'name:notCompanyName': 'Ha cég nevére kéri a számlát, akkor válassza a "Cég" opciót a "Magánszemély" helyett.',

			// password reset
			'password:current': 'Az új jelszó nem egyezhet meg a régivel.',

			//calculator
			'calculator:dimension.width.required': 'A szélesség megadása kötelező.',
			'calculator:dimension.width.number': 'Hibás szélesség.',
			'calculator:dimension.width.integer': 'Hibás szélesség.',
			'calculator:dimension.width.max': data => `A maximális szélesség: ${data['max']} cm.`,
			'calculator:dimension.width.min': data => `A minimális szélesség: ${data['min']} cm.`,

			'calculator:dimension.length.required': 'A hosszúság megadása kötelező.',
			'calculator:dimension.length.number': 'Hibás hosszúság.',
			'calculator:dimension.length.integer': 'Hibás hosszúság.',
			'calculator:dimension.length.max': data => `A maximális hosszúság: ${data['max']} cm.`,
			'calculator:dimension.length.min': data => `A minimális hosszúság: ${data['min']} cm.`,

			'calculator:dimension.height.required': 'A magasság megadása kötelező.',
			'calculator:dimension.height.number': 'Hibás magasság.',
			'calculator:dimension.height.integer': 'Hibás magasság.',
			'calculator:dimension.height.max': data => `A maximális magasság: ${data['max']} cm.`,
			'calculator:dimension.height.min': data => `A minimális magasság: ${data['min']} cm.`,

			'calculator:size:oversizedPackage': data => `A megadott adatokkal számolt ${data['actual']} cm körméret nagyobb, mint a maximális ${data['max']} cm.`,
			'calculator:size:undersizedPackage': data => `A megadott adatokkal számolt ${data['actual']} cm körméret kisebb, mint a minimális ${data['min']} cm.`,

			'calculator:codAmount.required': 'Az utánvét összegének megadása kötelező.',
			'calculator:codAmount.number': 'Hibás utánvét összeg.',
			'calculator:codAmount.max': data => `Az utánvét maximális összege  ${formatNumber(data['max'], this._locale)} Ft.`,
			'calculator:codAmount.min': data => `Az utánvét minimális összege  ${data['min']} Ft.`,
			'calculator:codAmount.tooFew': 'Az utánvét összege túl alacsony.',
			'calculator:codAmount.notRounded': 'Az utánvét csak 5 Ft-ra kerekített összeg lehet, azaz csak 0-ra vagy 5-re végződhet.',

			// parcel dispatch
			'zipCode:invalid': 'Az irányítószám mező csak számokat tartalmazhat.',
			'houseNumber:invalid': 'A házszám mező csak számokat tartalmazhat.',

			// 'email:already_exists': 'A megadott email címmel már létezik regisztráció.',

			'auth:failed': 'Sikertelen bejelentkezés.',

			'*:required': 'Kötelező mező.',
			'*:invalid': 'Érvénytelen mező.',

			required: 'Kötelező mező.',
			phoneNumber: `Kérjük, ellenőrizze a megadott telefonszámot. (Pl.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			bankAccountNumber: 'A bankszámlaszám nem megfelelő. (Kétszer vagy háromszor 8 számjegy, opcionálisan kötőjellel elválasztva.)',

			'firstMileType:invalid': 'A rendelés tartalmaz olyan csomagot, ami a méretei miatt nem adható fel GLS Automatában.',
		},
		en: {
			unknown_error: 'Unknown error!',
			'app:error': 'Unknown error! Please try again later!',
			'parcel_number:invalid': 'Invalid parcel number!',
			'parcel:notFound': 'Parcel not found.',
			'parcel:not_found': 'Parcel not found.',

			'name:required': 'Required field.',
			'email:required': 'Required field.',
			'email:invalidEmail': 'Invalid e-mail address.',
			'email:email': 'Invalid e-mail address.',
			'mobile:required': 'Required field.',
			'mobile:invalidPhone': `Invalid phone number. (e.g.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:phoneNumber': `Invalid phone number. (e.g.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:myglsPhoneNumber': `Invalid phone number.`,
			'mobile:myglsPhoneNumber.any': `Invalid phone number. (e.g.: ${Validators.getMyglsPhoneNumberExample('any')})`,
			'mobile:myglsPhoneNumber.hu': `Invalid phone number. (in Hungary: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			'mobile:myglsPhoneNumber.sk': `Invalid phone number. (in Slovakia: ${Validators.getMyglsPhoneNumberExample('sk')})`,
			'mobile:myglsPhoneNumber.cz': `Invalid phone number. (in the Chech Republic: ${Validators.getMyglsPhoneNumberExample('cz')})`,
			'mobile:myglsPhoneNumber.ro': `Invalid phone number. (in Romania: ${Validators.getMyglsPhoneNumberExample('ro')})`,
			'mobile:myglsPhoneNumber.si': `Invalid phone number. (in Slovania: ${Validators.getMyglsPhoneNumberExample('si')})`,
			'mobile:myglsPhoneNumber.hr': `Invalid phone number. (in Croatia: ${Validators.getMyglsPhoneNumberExample('hr')})`,

			// frontend validation
			'password:required': 'Required field.',
			'password:weakPassword': 'This password is too weak.',
			'password:weakPassword:length': 'The password is too short, minimum 8 characters.',
			'password:weakPassword:number': 'The password must contain a number.',
			'password:weakPassword:lowercase': 'The password must contain a lowercase letter.',
			'password:weakPassword:uppercase': 'The password must contain an uppercase letter.',
			'password:weakPassword:symbol': 'The password must contain a special character.',
			'passwordConfirmation:required': 'Required field.',
			'passwordConfirmation:passwordMismatch': "The two passwords don't match.",
			// backend validation
			'password:numbers:required': 'The password must contain a number.',
			'password:mixedCase:required': 'The password must contain a lowercase and an uppercase letter.',
			'password:symbols:required': 'The password must contain a special character.',
			'password:recentlyUsed': 'This password has been used recently.',
			'password:compromised': 'This password is too weak.',
			'password:password:numbers:required': 'The password must contain a number.',
			'password:password:mixedCase:required': 'The password must contain a lowercase and an uppercase letter.',
			'password:password:symbols:required': 'The password must contain a special character.',
			'password:password:recentlyUsed': 'This password has been used recently.',
			'password:password:compromised': 'This password is too weak.',

			'bankAccountHolder:required': "The backaccount holder's name is required.",
			'bankAccountNumber:required': 'The bank account number is required.',
			'bankAccountNumber:invalidFormat': 'Invalid bank account number. (Eight digits two or three times, optionally separated by dashes.)',
			'bankAccountNumber:invalid': "This bank account number doesn't seem to be a valid Hungarian bank account.",

			'taxNumber:taxNumber': 'Invalid tax number. Expected format: 12345678-1-12',
			'zipCode:zipCode': 'Zip code may only contain digits.',
			'zipCode:zipCode:invalidFormat': data => `Invalid format. (${data['format']})`,
			'zipCode:minLength': data => `Zip code is min. ${data['minLength']} characters.`,
			'zipCode:maxLength': data => `Zip code is max. ${data['maxLength']} characters.`,
			'zipCode:deliveryAddressZipCode:restricted': 'Shippig to this Zip code is not allowed.',
			'houseNumber:houseNumber': 'House number may only contain digits.',
			'name:notCompanyName': 'If you require the invoice at company name, please choose the option „Company” instead of „Private Individual”.',

			// password reset
			'password:current': 'The new password must be different from the old one.',

			//calculator
			'calculator:dimension.width.required': 'Width is required.',
			'calculator:dimension.width.number': 'Invalid width.',
			'calculator:dimension.width.integer': 'Invalid width.',
			'calculator:dimension.width.max': data => `Max. width: ${data['max']} cm.`,
			'calculator:dimension.width.min': data => `Min. width: ${data['min']} cm.`,

			'calculator:dimension.length.required': 'Length is required.',
			'calculator:dimension.length.number': 'Invalid length.',
			'calculator:dimension.length.integer': 'Invalid length.',
			'calculator:dimension.length.max': data => `Max. length: ${data['max']} cm.`,
			'calculator:dimension.length.min': data => `Min. length: ${data['min']} cm.`,

			'calculator:dimension.height.required': 'Height is required.',
			'calculator:dimension.height.number': 'Invalid height.',
			'calculator:dimension.height.integer': 'Invalid height.',
			'calculator:dimension.height.max': data => `Max. height: ${data['max']} cm.`,
			'calculator:dimension.height.min': data => `Min. height: ${data['min']} cm.`,

			'calculator:size:oversizedPackage': data => `The calculated ${data['actual']} cm girth is greater than the max. ${data['max']} cm.`,
			'calculator:size:undersizedPackage': data => `The calculated ${data['actual']} cm girth is less than the min. ${data['max']} cm.`,

			'calculator:codAmount.required': 'COD amount is required.',
			'calculator:codAmount.number': 'Invalid COD amount.',
			'calculator:codAmount.max': data => `Max. COD amount: HUF ${formatNumber(data['max'], this._locale)}.`,
			'calculator:codAmount.min': data => `Min. COD amount: HUF ${data['min']}.`,
			'calculator:codAmount.tooFew': 'COD amount is too low.',
			'calculator:codAmount.notRounded': 'COD amount must be rounded to HUF 5.',

			// parcel dispatch
			'zipCode:invalid': 'Zip code may only contain digits.',
			'houseNumber:invalid': 'House number may only contain digits.',

			// 'email:already_exists': 'A megadott email címmel már létezik regisztráció.',

			'auth:failed': 'Login failed.',

			'*:required': 'Required field.',
			'*:invalid': 'Invalid field.',

			required: 'Required field.',
			phoneNumber: `Invalid phone number. (e.g.: ${Validators.getMyglsPhoneNumberExample('hu')})`,
			bankAccountNumber: 'Invalid bank account number. (Eight digits two or three times, optionally separated by dashes.)',
		},
	};

	private wildcardRegex = /^(?:[^\*]+):(?<fixed>required|invalid)$/;

	constructor(@Inject(LOCALE_ID) private _locale: string, private _translate: TranslateService) {}

	public get(error: string | Error, useCodeAsFallback: boolean = true): string {
		const lang = Object.keys(this.errors).includes(this._translate.currentLang) ? this._translate.currentLang : 'hu';
		let errorCode = 'unknown_error';

		if (typeof error === 'string') {
			errorCode = error;
		} else {
			if (error.message) {
				return error.message;
			} else if (error.code) {
				errorCode = error.code;
			} else {
				console.error(`error code or message not found.`, error);
				Sentry.withScope(scope => {
					scope.setTag('module', 'ErrorService');
					scope.setExtra('error', error);
					Sentry.captureMessage('Error code or message not found');
				});
			}
		}

		const errorMessage = this.errors[lang][errorCode];
		if (typeof errorMessage === 'string') {
			return errorMessage;
		} else if (typeof errorMessage === 'function') {
			return errorMessage(isError(error) ? error['data'] : null);
		} else if (this.wildcardRegex.test(errorCode)) {
			const wildcardErrorCode = errorCode.replace(this.wildcardRegex, '*:$1');
			console.log('wildcardify:', errorCode, wildcardErrorCode);
			return this.get(wildcardErrorCode);
		} else if (useCodeAsFallback) {
			return errorCode;
		} else {
			console.error(`error message not found: ${errorCode}`);
			Sentry.withScope(scope => {
				scope.setTag('module', 'ErrorService');
				scope.setExtra('error', error);
				Sentry.captureMessage('Error message not found');
			});
			return this.get('unknown_error');
		}
	}

	public getFieldName(errorMsg: string): string {
		const fields = errorMsg.split(':');
		let field;

		if (fields.length) {
			field = fields[0];
			if (field === 'date_of_birth' || field === 'over18') {
				field = 'year';
			}
			return field;
		}

		return '';
	}

	public getErrorCode(errorCode: string): string {
		const fields = errorCode.split(':');
		let code = '';

		if (fields.length) {
			code = fields[1];
		}

		return code;
	}

	/**
	 * Get all the error codes and messages for a field
	 */
	public getErrorMessagesForField(field: string): object[] {
		const lang = Object.keys(this.errors).includes(this._translate.currentLang) ? this._translate.currentLang : 'hu';

		const fieldErrorMessages: object[] = [];
		for (const errorCode in this.errors) {
			if (this.getFieldName(errorCode) === field) {
				fieldErrorMessages.push({
					errorCode: this.getErrorCode(errorCode),
					errorMessage: this.errors[lang][errorCode],
				});
			}
		}
		return fieldErrorMessages;
	}

	protected parseTranslation(): void {
		for (const translation in this.translations) {
			if (translation.includes('error')) {
				console.log(translation);
			}
		}
	}
}
